import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { Order, OrderStatus, OrderItem } from '@rosita/shared';
import { SEO } from '../../seo/useSEO';
import { t } from '../../i18n/pt';
import { api, apiBaseUrl } from '../../lib/api';
import { formatDate, formatEur } from '../../lib/format';
import { AccountPageHeader } from '../../components/account/AccountPageHeader';

interface OrderDetail extends Order {
  items: OrderItem[];
}

export default function OrderDetailPage(): JSX.Element {
  const { orderNumber } = useParams<{ orderNumber: string }>();
  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void (async () => {
      try {
        const response = await api.get<OrderDetail>(`/orders/${orderNumber}`);
        setOrder(response.data);
      } finally {
        setLoading(false);
      }
    })();
  }, [orderNumber]);

  if (loading) return <p>{t.common.loading}</p>;
  if (!order) return <p>Encomenda não encontrada.</p>;

  const apiBase = apiBaseUrl;

  return (
    <>
      <SEO title={`Encomenda ${order.orderNumber}`} noindex />
      <AccountPageHeader
        title={`Encomenda ${order.orderNumber}`}
        lead={`${formatDate(order.createdAt)} · ${t.orderStatus[order.status as OrderStatus]}`}
      />

      <h2>Itens</h2>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th align="left" style={{ padding: 8, borderBottom: '2px solid #111' }}>Produto</th>
            <th style={{ padding: 8, borderBottom: '2px solid #111' }}>Qtd.</th>
            <th align="right" style={{ padding: 8, borderBottom: '2px solid #111' }}>Total</th>
          </tr>
        </thead>
        <tbody>
          {order.items.map((i) => (
            <tr key={i.id}>
              <td style={{ padding: 8, borderBottom: '1px solid #eee' }}>
                {i.productName}<br />
                <small style={{ color: '#666' }}>{i.variantSize} · {i.variantColour}</small>
              </td>
              <td align="center" style={{ padding: 8, borderBottom: '1px solid #eee' }}>{i.quantity}</td>
              <td align="right" style={{ padding: 8, borderBottom: '1px solid #eee' }}>{formatEur(i.lineTotal)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <dl style={{ marginTop: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <dt>Subtotal</dt><dd>{formatEur(order.subtotal)}</dd>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <dt>Portes</dt><dd>{formatEur(order.shippingCost)}</dd>
        </div>
        {Number(order.discountAmount) > 0 ? (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <dt>Desconto</dt><dd>-{formatEur(order.discountAmount)}</dd>
          </div>
        ) : null}
        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, borderTop: '1px solid #ddd', paddingTop: 8, marginTop: 8 }}>
          <dt>Total</dt><dd>{formatEur(order.total)}</dd>
        </div>
      </dl>

      <h2>Morada de envio</h2>
      <address>
        {order.shippingFirstName} {order.shippingLastName}<br />
        {order.shippingAddress1}<br />
        {order.shippingAddress2 ? <>{order.shippingAddress2}<br /></> : null}
        {order.shippingPostalCode} {order.shippingCity}<br />
        {order.shippingDistrict}, {order.shippingCountry}
      </address>

      {order.paymentStatus === 'paid' ? (
        <p>
          <a href={`${apiBase}/api/v1/orders/${order.orderNumber}/invoice`} target="_blank" rel="noreferrer">
            {t.account.downloadInvoice}
          </a>
        </p>
      ) : null}
    </>
  );
}
